import "../index.scss";

import * as React from "react";

import ElementEditorLayout from "@/v1/app/ElementEditorLayout";
import TemplateLibraryEditorLayout from "@/v1/app/TemplateLibraryEditorLayout";
import { useApplySharedTemplateAfterAuth } from "@/v1/hooks/useApplySharedTemplateAfterAuth";
import Pages from "@/v1/pages/site/Pages";
// Agent UI imports
import AgentReactEditor from "@agent-ui/features/chat/components/ReactEditor";
import AgentChatLayout from "@agent-ui/features/chat/layouts/ChatLayout";
import { CurrentChatProvider as AgentCurrentChatProvider } from "@agent-ui/features/chat/providers/CurrentChatContext";
import { ChatProvider as AgentChatStoreProvider } from "@agent-ui/features/chat/store/ChatStoreContext";
import { SessionProvider as AgentSessionProvider } from "@agent-ui/features/chat/store/SessionStoreContext";
import AgentHome from "@agent-ui/features/home/components/Home";
import { ModifiersProvider as AgentModifiersProvider } from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { NextDevServerProvider as AgentNextDevServerProvider } from "@agent-ui/features/page-renderer/components/code-execution/context";
import { EditorStoreProvider as AgentEditorStoreProvider } from "@agent-ui/store/editorStore";
import { SandboxStateProvider as AgentSandboxStateProvider } from "@agent-ui/store/SandboxStateStoreContext";
import { TRPCProviders } from "@app/trpcProviders";
import { Analytics as DubAnalytics } from "@dub/analytics/react";
import { CommandMenu } from "@v1/components/CommandMenu";
import SentryBoundary from "@v1/components/common/SentryBoundary";
import AllProjects from "@v1/components/dashboard/projects/AllProjects";
import { BrandDetailsProvider } from "@v1/components/designLibrary/BrandDetailsContext";
import { ToastManager } from "@v1/components/designSystem/alert/Toast";
import NoAccessToProjectLayout from "@v1/components/layouts/errors/NoAccessToProjectLayout";
import ShopifyInstalledOnDifferentProjectLayout from "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnDifferentProjectLayout";
import ShopifyInstalledOnExistingWorkspaceLayout from "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnExistingWorkspaceLayout";
import ShopifyInstalledOnHostingBilledThroughShopifyLayout from "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnHostingBilledThroughShopifyLayout";
import ShopifyInstalledOnNoAccessWorkspaceLayout from "@v1/components/layouts/integrations/shopify/ShopifyInstalledOnNoAccessWorkspaceLayout";
import ShopifyPendingAppInstallationLayout from "@v1/components/layouts/integrations/shopify/ShopifyPendingAppInstallationLayout";
import ShareTemplateLayout from "@v1/components/layouts/ShareTemplateLayout";
import { AnalyticsHome } from "@v1/features/analytics/AnalyticsHome";
import { AnalyticsOverview } from "@v1/features/analytics/AnalyticsOverview";
import { AnalyticsRedirect } from "@v1/features/analytics/AnalyticsRedirect";
import AnalyticsInsights from "@v1/features/analytics/insights/AnalyticsInsights";
import { InsightsRedirect } from "@v1/features/analytics/insights/InsightsRedirect";
import PageDetails from "@v1/features/analytics/PageDetails";
import { ProjectAnalyticsDashboard } from "@v1/features/analytics/WorkspaceAnalyticsDashboard";
import ExperimentEditWrapper from "@v1/features/experiments/ExperimentEditWrapper";
import Experiments from "@v1/features/experiments/Experiments";
import { ExperimentsRedirect } from "@v1/features/experiments/ExperimentsRedirect";
import ExperimentDetailsTabV2 from "@v1/features/experiments/tabs/ExperimentDetailsTab";
import ExperimentResultsTab from "@v1/features/experiments/tabs/ExperimentResultsTab";
import { V1AIStreamingProvider } from "@v1/providers/AIStreamingProvider";
import { store } from "@v1/store/store";
import { getLastSelectedElementIdForProject } from "@v1/utils/localStorage";
import { FullScreenLoader } from "@v2/components/FullScreenLoader";
import CurrentProjectProvider, {
  useCurrentProjectContext,
  useIsAgentEnabled,
} from "@v2/contexts/CurrentProjectContext";
import { CurrentUserProvider } from "@v2/contexts/CurrentUserContextProvider";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { AuthLayout } from "@v2/features/account/AuthLayout";
import GoogleAuthProxy from "@v2/features/account/GoogleAuthProxy";
import InitResetPassword from "@v2/features/account/InitResetPassword";
import LoginForm from "@v2/features/account/LoginForm";
import ResetPassord from "@v2/features/account/ResetPasswordForm";
import SignupForm from "@v2/features/account/SignupForm";
import Billing from "@v2/features/billing/Billing";
import { WorkspaceBillingRedirectRoute } from "@v2/features/billing/WorkspaceBillingRedirectRoute";
import WorkspaceDashboard from "@v2/features/billing/WorkspaceDashboard";
import ReactEditor from "@v2/features/chat/components/ReactEditor";
import ChatLayout from "@v2/features/chat/layouts/ChatLayout";
import { CurrentChatProvider } from "@v2/features/chat/providers/CurrentChatContext";
import { ChatProvider } from "@v2/features/chat/store/ChatStoreContext";
import DashboardLayout from "@v2/features/dashboard/DashboardLayout";
import BackendStagingBanner from "@v2/features/debug/BackendStagingBanner";
import { DesignLibrary } from "@v2/features/design-library/DesignLibrary";
import { MyReferrals } from "@v2/features/dub/MyReferrals";
import Home from "@v2/features/home/components/Home";
import { AgentIntegrationDetailPage } from "@v2/features/integrations/AgentIntegrationDetailPage";
import { EnabledIntegrationsPage } from "@v2/features/integrations/EnabledIntegrationsPage";
import HootsuiteContentSourceLayout from "@v2/features/integrations/hootsuite/HootsuiteContentSourceLayout";
import { IntegrationDetailPage } from "@v2/features/integrations/IntegrationDetailPage";
import { IntegrationsPage } from "@v2/features/integrations/IntegrationsPage";
import StarterProjectsModal from "@v2/features/new-shop-onboarding/StarterProjectsModal";
import { useSelectInitialProjectSetupModal } from "@v2/features/onboarding/useSelectInitialProjectSetupModal";
import WorkspaceWrappedOnboarding from "@v2/features/onboarding/WorkspaceWrappedOnboarding";
import { NextDevServerProvider } from "@v2/features/page-renderer/components/code-execution/context";
import { ProductEditTab } from "@v2/features/products/ProductEditTab";
import { ProductEditWrapper } from "@v2/features/products/ProductEditWrapper";
import { ProductsDashboard } from "@v2/features/products/ProductsDashboard";
import Profile from "@v2/features/settings/Profile";
import AbTestingSettings from "@v2/features/settings/shop/AbTesting";
import FontManager from "@v2/features/settings/shop/FontManager";
import ShopGeneralSettings from "@v2/features/settings/shop/General";
import Integrations from "@v2/features/settings/shop/Integrations";
import IntegrationsEdit from "@v2/features/settings/shop/integrations/IntegrationsEdit";
import IntegrationsHome from "@v2/features/settings/shop/integrations/IntegrationsHome";
import ScriptManager from "@v2/features/settings/shop/ScriptManager";
import ShopSettings from "@v2/features/settings/ShopSettings";
import WorkspaceSettings from "@v2/features/settings/workspace/General";
import WorkspaceMembers from "@v2/features/settings/workspace/Members";
import SharePageTemplateLayout from "@v2/features/share-page-template/SharePageTemplateLayout";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import {
  LocalStorageProvider as AgentLocalStorageProvider,
  LocalStorageProvider,
  useLocalStorageState,
} from "@v2/hooks/useLocalStorage";
import { EditorStoreProvider } from "@v2/store/editorStore";
import { useModal } from "@v2/store/modalStore";
import { SandboxStateProvider } from "@v2/store/SandboxStateStoreContext";
import { marketingRouter } from "@v2/trpc/routes/marketing";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { Agentation } from "agentation";
import { CookiesProvider } from "react-cookie";
import { Provider as ReduxProvider } from "react-redux";
import {
  createBrowserRouter,
  createRoutesFromElements,
  generatePath,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { Toaster } from "sonner";
import { z } from "zod";

import AppModals from "./AppModals";
import AuthenticatedUserRequiredRoute from "./AuthenticatedUserRequiredRoute";
import { DebugCommandMenuItemsProvider } from "./DebugCommandMenuItemsProvider";
import { ScopedAnalyticsProvider } from "./ScopedAnalyticsProvider";
import { ShopsRedirectRoute } from "./ShopsRedirectRoute";

/**
 * Paths in onboarding that the user should ONLY see if they have exactly ZERO projects
 * they can access on their account (i.e., they just started and have no projects yet,
 * and have not been invited to one)
 */
const onboardingZeroProjectOnlyPaths = [routes.onboarding.createShop];

/**
 * Whether a given url path is part of onboarding
 */
const isOnboardingPath = (pathname: string) => {
  return pathname.startsWith("/onboarding");
};

/**
 * Component which redirects away from specific routes based on the current project type.
 * I.e. if you access /chat on a v1 project we want to redirect them back to the site pages
 * at /site. The /chat should be wrapped in this component with the specific type in this case.
 */
const RedirectBasedOnProjectType: React.FC<
  React.PropsWithChildren<{
    type:
      | "v2Home"
      | "v1SitePages"
      | "v2Chat"
      | "v2DesignLibrary"
      | "v2StyleExtraction";
  }>
> = ({ children, type }) => {
  const { project } = useCurrentProjectContext();
  const isAgentEnabled = useIsAgentEnabled();

  if (!project) {
    return <>{children}</>;
  }

  if (project.hostingType === "replo" && type === "v1SitePages") {
    const homeRoute = isAgentEnabled ? routes.agent.home : routes.shops.home;
    const homePath = generatePath(homeRoute, { projectId: project.id });
    return <Navigate to={homePath} replace />;
  }

  if (project.hostingType === "replo" && isAgentEnabled) {
    if (type === "v2Home") {
      return (
        <Navigate
          to={generatePath(routes.agent.home, { projectId: project.id })}
          replace
        />
      );
    }

    if (type === "v2Chat") {
      return (
        <Navigate
          to={generatePath(routes.agent.chat, { projectId: project.id })}
          replace
        />
      );
    }
  }

  if (project.hostingType !== "replo" && ["v2Home", "v2Chat"].includes(type)) {
    // Note (Noah, 2025-09-18): If they're trying to access v2 home or chat but we're a v1 project,
    // we'll redirect them to v1 site pages
    return (
      <Navigate
        to={generatePath(routes.shops.site, { projectId: project.id })}
        replace
      />
    );
  }

  return <>{children}</>;
};

export function App() {
  return (
    <SentryBoundary>
      <ReduxProvider store={store}>
        <TRPCProviders>
          <DubAnalytics
            domainsConfig={{
              refer: "try.replo.app",
            }}
            queryParams={["via", "ref"]}
            cookieOptions={{ domain: ".replo.app" }}
          />
          <RouterProvider router={router} />
          <Toaster />
          {shouldShowV2Debuggers() && <Agentation />}
        </TRPCProviders>
      </ReduxProvider>
    </SentryBoundary>
  );
}

const router = (() => {
  return createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RootRoute />}>
        {/* Index redirect route */}
        <Route index element={<RootRouteIndexRedirectRoute />} />
        {/* Google auth proxy for Vercel preview deployments (outside AuthLayout) */}
        <Route path="/auth/google-proxy" element={<GoogleAuthProxy />} />
        {/* Auth routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route index element={<Navigate to={routes.signup} replace />} />
          <Route path="login" element={<LoginForm />} />
          <Route path="signup" element={<SignupForm />} />
          <Route path="password/reset/init" element={<InitResetPassword />} />
          <Route path="password/reset" element={<ResetPassord />} />
        </Route>
        {/* Public template sharing route */}
        <Route path={routes.share.page} element={<SharePageTemplateLayout />} />
        {/* Redirect routes */}
        <Route
          path="/partner/program"
          element={<PartnerProgramRedirectRoute />}
        />
        <Route
          path="/workspace/billing"
          element={<WorkspaceBillingRedirectRoute />}
        />
        <Route element={<ShopsRedirectRoute />}>
          <Route path={routes.home.root} element={<ShopsRedirectRoute />} />
          <Route
            path={routes.home.shareTemplate}
            element={<ShopsRedirectRoute />}
          />

          {/* Project routes */}
          <Route
            path={routes.allProjects}
            element={
              <AuthenticatedUserRequiredRoute>
                <AllProjects>
                  <AppModals />
                </AllProjects>
              </AuthenticatedUserRequiredRoute>
            }
          />

          {/* Workspace routes */}
          <Route
            path="/workspace/:workspaceId"
            element={
              <AuthenticatedUserRequiredRoute>
                <WorkspaceDashboard />
              </AuthenticatedUserRequiredRoute>
            }
          >
            <Route index element={<WorkspaceIndexRedirectRoute />} />
            <Route path="settings" element={<ShopSettings />}>
              <Route index element={<Navigate to="general" replace />} />
              <Route path="general" element={<WorkspaceSettings />} />
              <Route path="members" element={<WorkspaceMembers />} />
              <Route path="billing" element={<Billing />} />
            </Route>
          </Route>
          {/* Analytics workspace agnostic route */}
          <Route path="/analytics" element={<AnalyticsRedirect />} />
          {/* Experiments workspace agnostic route */}
          <Route path="/abtesting" element={<ExperimentsRedirect />} />
          {/* Insights workspace agnostic route */}
          <Route path="/analytics/insights" element={<InsightsRedirect />} />
          {/* Editor routes */}
          <Route path="/editor/:projectId" element={<ProjectRouteWrapper />}>
            <Route index element={<EditorIndexRedirect />} />
            <Route
              path="new"
              element={
                <TemplateLibraryEditorLayout>
                  <AppModals />
                </TemplateLibraryEditorLayout>
              }
            />
            <Route
              path=":elementId"
              element={
                <ElementEditorLayout>
                  <AppModals />
                </ElementEditorLayout>
              }
            />
          </Route>

          {/* Onboarding routes */}
          <Route path="/onboarding">
            <Route
              index
              element={<Navigate to="/onboarding/setup" replace />}
            />
            <Route
              path=":onboardingStep"
              element={
                <AuthenticatedUserRequiredRoute>
                  <WorkspaceWrappedOnboarding>
                    <AppModals />
                  </WorkspaceWrappedOnboarding>
                </AuthenticatedUserRequiredRoute>
              }
            />
          </Route>
        </Route>
        <Route
          path="/template/:templateId"
          element={
            <AuthenticatedUserRequiredRoute>
              <ShareTemplateLayout />
            </AuthenticatedUserRequiredRoute>
          }
        />
        {/* Shops routes */}
        <Route
          path="/shops/:projectId"
          element={
            <AuthenticatedUserRequiredRoute>
              <ToastManager />
              <ProjectRouteWrapper />
            </AuthenticatedUserRequiredRoute>
          }
        >
          <Route index element={<Navigate to="site" replace />} />

          {/* Shops with dashboard layout */}
          <Route element={<DashboardLayout contentIsCard={true} />}>
            <Route path="settings/profile" element={<Profile />} />
            <Route path="settings/referrals" element={<MyReferrals />} />
            {/* /site */}
            <Route
              path="site"
              element={
                <RedirectBasedOnProjectType type="v1SitePages">
                  <Pages />
                </RedirectBasedOnProjectType>
              }
            />

            <Route path="home">
              <Route
                index
                element={
                  <RedirectBasedOnProjectType type="v2Home">
                    <Home />
                  </RedirectBasedOnProjectType>
                }
              />
            </Route>

            {/* /analytics */}
            <Route path="analytics" element={<ProjectAnalyticsDashboard />}>
              <Route path="home" element={<AnalyticsHome />} />
              <Route path="overview" element={<AnalyticsOverview />} />
              <Route path="deep-dive" element={<PageDetails />} />
              <Route path="insights" element={<AnalyticsInsights />} />
            </Route>

            {/* /abtesting */}
            <Route path="abtesting" element={<Experiments />} />
            <Route
              path="abtesting/:experimentId"
              element={<ExperimentEditWrapper />}
            >
              <Route
                path={routes.shops.experiments.details}
                element={<ExperimentDetailsTabV2 />}
              />
              <Route
                path={routes.shops.experiments.results}
                element={<ExperimentResultsTab />}
              />
            </Route>

            {/* /design-library */}
            <Route
              path="design-library"
              element={
                <RedirectBasedOnProjectType type="v2DesignLibrary">
                  <DesignLibrary />
                </RedirectBasedOnProjectType>
              }
            />

            {/* /products */}
            <Route path="products" element={<ProductsDashboard />} />

            {/* /integrations */}
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route
              path="integrations/enabled"
              element={<EnabledIntegrationsPage />}
            />
            <Route
              path="integrations/:integrationType"
              element={<IntegrationDetailPage />}
            />
            <Route
              path="integrations/agent/:integrationKey"
              element={<AgentIntegrationDetailPage />}
            />

            {/* /settings */}
            <Route path="settings">
              <Route index element={<Navigate to="shop/general" replace />} />
              {/* /settings/shop */}
              <Route path="shop" element={<ShopSettings />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<ShopGeneralSettings />} />
                <Route path="fonts" element={<FontManager />} />
                <Route path="abtesting" element={<AbTestingSettings />} />
                <Route path="scripts" element={<ScriptManager />} />
                <Route path="integrations" element={<Integrations />}>
                  <Route index element={<Navigate to="home" replace />} />
                  <Route path="home" element={<IntegrationsHome />} />
                  <Route
                    path=":integrationId/edit"
                    element={<IntegrationsEdit />}
                  />
                </Route>
              </Route>
              {/* /settings/workspace */}
              <Route path="workspace" element={<ShopSettings />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<WorkspaceSettings />} />
                <Route path="members" element={<WorkspaceMembers />} />
                <Route path="billing" element={<Billing />} />
              </Route>
            </Route>
          </Route>

          <Route element={<DashboardLayout contentIsCard={false} />}>
            <Route element={<ChatLayout />}>
              <Route
                path="chat"
                element={
                  <RedirectBasedOnProjectType type="v2Chat">
                    <ReactEditor />
                  </RedirectBasedOnProjectType>
                }
              />
            </Route>
            <Route element={<ProductEditWrapper />}>
              {
                // NOTE (Kurt, 2025-09-01): We put this products edit route in the
                // contentIsCard = false layout because we want the product edits screen
                // to have two seperate cards instead of a unified one.
              }
              <Route
                path="products/:productId/edit"
                element={<ProductEditTab />}
              />
              <Route path="products/new" element={<ProductEditTab />} />
            </Route>
          </Route>
        </Route>

        {/* Agent routes */}
        <Route
          path={routes.agent.root}
          element={
            <AuthenticatedUserRequiredRoute>
              <ToastManager />
              <AgentProjectRouteWrapper />
            </AuthenticatedUserRequiredRoute>
          }
        >
          <Route index element={<Navigate to="home" replace />} />
          <Route element={<DashboardLayout contentIsCard={true} />}>
            <Route path="home" element={<AgentHome />} />
          </Route>
          <Route element={<DashboardLayout contentIsCard={false} />}>
            <Route element={<AgentChatLayout />}>
              <Route path="chat" element={<AgentReactEditor />} />
            </Route>
          </Route>
        </Route>

        {/* Error routes */}
        <Route path="error">
          <Route path="no-access" element={<NoAccessToProjectLayout />} />
        </Route>

        {/* Integrations routes */}
        <Route path="shopify">
          <Route
            path="install"
            element={<ShopifyPendingAppInstallationLayout />}
          />
          <Route path="error">
            <Route
              path="different-project"
              element={<ShopifyInstalledOnDifferentProjectLayout />}
            />
            <Route
              path="different-workspace"
              element={<ShopifyInstalledOnExistingWorkspaceLayout />}
            />
            <Route
              path="no-access-workspace"
              element={<ShopifyInstalledOnNoAccessWorkspaceLayout />}
            />
            <Route
              path="hosting-billed-through-shopify"
              element={<ShopifyInstalledOnHostingBilledThroughShopifyLayout />}
            />
          </Route>
        </Route>

        {/* External integrations routes */}
        <Route path="external-integrations">
          <Route path="hootsuite" element={<HootsuiteContentSourceLayout />} />
        </Route>

        {/*  404 redirect route */}
        <Route path="*" element={<Navigate to={routes.home.root} replace />} />
      </Route>,
    ),
  );
})();

function RootRoute() {
  return (
    <RootRouteProviders>
      <RootRouteLayout>
        <Outlet />
      </RootRouteLayout>
    </RootRouteProviders>
  );
}

function RootRouteProviders({ children }: React.PropsWithChildren) {
  return (
    // NOTE (Martin, 2024-10-09): We need an extra SentryBoundary component
    // here so that Sentry can catch errors inside the routing tree before
    // the React Router Boundary does it.
    <SentryBoundary>
      <CookiesProvider defaultSetOptions={{ path: "/" }}>
        <LocalStorageProvider>
          <CurrentUserProvider>
            <V1AIStreamingProvider>
              <BrandDetailsProvider>
                <ChatProvider>{children}</ChatProvider>
              </BrandDetailsProvider>
            </V1AIStreamingProvider>
          </CurrentUserProvider>
        </LocalStorageProvider>
      </CookiesProvider>
    </SentryBoundary>
  );
}

/**
 * Loads Google Tag Manager on all dashboard pages in production.
 *
 * @author Ben Vogt 2025-05-02
 */
function useGoogleTagManager() {
  React.useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return;
    }

    const scriptHTML = `
      <!-- Google Tag Manager -->
      <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-MH56PQ8');</script>
      <!-- End Google Tag Manager -->
    `;

    const tempElement = document.createElement("div");
    tempElement.innerHTML = scriptHTML.trim();
    if (tempElement.firstChild) {
      document.head.append(tempElement.firstChild);
    }
  }, []);
}

function PylonChatHider() {
  const { pathname } = useLocation();
  const isOnboarding = pathname.includes("/onboarding");
  const isSharedTemplateRoute = pathname.startsWith("/share/page/");

  if (!isOnboarding && !isSharedTemplateRoute) {
    return null;
  }

  return <style>{`.PylonChat { display: none !important; }`}</style>;
}

function RootRouteLayout({ children }: React.PropsWithChildren) {
  const { closeModalsWithExclusions } = useModal();
  const [isUsingBackendStaging] = useLocalStorageState(
    "replo.debug.useBackendStaging",
    false,
    { schema: z.boolean() },
  );

  const { pathname } = useLocation();
  const { user, isLoading: isLoadingUser, isAuthenticated } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const hash = window.location.hash;
  const seedToken = hash.startsWith("#seed=")
    ? decodeURIComponent(hash.slice(6))
    : null;

  const { isLoading: isLoadingMarketingSeed } =
    marketingRouter.getMarketingWebsiteSeedData.useQuery(
      { seed: seedToken! },
      {
        enabled: Boolean(seedToken),
        select: (seedData) => {
          if (seedData) {
            localStorage.setItem(
              "replo_marketing_seed",
              JSON.stringify({
                prompt: seedData.prompt,
                jti: seedData.jti,
                hasImage: Boolean(seedData.imageUrl),
              }),
            );
            window.history.replaceState(
              null,
              "",
              window.location.pathname + window.location.search,
            );
          }
          return seedData;
        },
      },
    );

  useOnValueChange(pathname, () => {
    closeModalsWithExclusions({
      typesToExclude: [
        "deleteSwatchConfirmationModal",
        "deleteElementConfirmationModal",
      ],
    });
  });

  useGoogleTagManager();

  if (isLoadingUser || isLoadingMarketingSeed) {
    return <FullScreenLoader />;
  }

  // NOTE (Martin, 2024-10-03): if we are not in auth routes and the user is not
  // authenticated, we redirect to the signup page.
  // Note (Ezra, 2025-11-11): We exclude /share/page/ from the auth redirect
  // because template sharing pages should be publicly accessible without requiring
  // authentication.
  // Note (Noah, 2026-02-20): We exclude /external-integrations/ from the auth redirect
  // because external integration pages (e.g., Hootsuite content source) should be
  // publicly accessible without requiring authentication.
  if (
    !pathname.startsWith("/auth") &&
    !pathname.startsWith("/share/page/") &&
    !pathname.startsWith("/external-integrations/") &&
    !isLoadingUser &&
    !isAuthenticated
  ) {
    const allParams = searchParams.toString();
    const redirectUrl = `${routes.signup}${allParams ? `?${allParams}` : ""}`;
    return <Navigate to={redirectUrl} replace />;
  }

  // NOTE (Ben O. 2025-04-28): If the user hasn't completed onboarding, we redirect to the onboarding page.
  // this doesn't apply if we are redirecting after a Shopify integration from onboarding. useConnectShopifyIntegration
  // handles the redirection for that.

  // NOTE (Berkley, 2025-09-10): This is the state check for whether the user has completed onboarding
  // First with check if the user is associated with a project or workspace with a project. Basically, is there a project they can go to?
  // If so, we do not redirect, and let them continue to their route
  // If not, we check if the user has a first and last name. If not, we direct them to user setup step
  // If they do, we direct them to the project setup step.
  const userHasAccessToAnyProject = Boolean(user?.defaultProjectId);

  // Note (Noah, 2025-09-26): If the user has access to any project and this happens to be a
  // route that we only want them to see if they have no projects, then redirect them to home
  // which will redirect them to their first project.
  const shouldDirectUserToProject =
    isAuthenticated &&
    !isLoadingUser &&
    onboardingZeroProjectOnlyPaths.some((path) => pathname.startsWith(path)) &&
    userHasAccessToAnyProject;
  if (shouldDirectUserToProject) {
    return <Navigate to={routes.shops.default} replace />;
  }

  // Note (Noah, 2025-09-26): If we're not in an onboarding route, but the user has zero projects,
  // when we need to redirect them back to onboarding. This is e.g. if the user somehow accesses
  // dashboard.replo.app/home after dropping out of the onboarding flow. We redirect to a different
  // part of the flow depending on what info they have filled out on their user object
  const doesUserHaveName = user?.firstName || user?.lastName;
  const shouldDirectUserToOnboarding =
    isAuthenticated &&
    !isLoadingUser &&
    !isOnboardingPath(pathname) &&
    (!doesUserHaveName || !userHasAccessToAnyProject);

  if (shouldDirectUserToOnboarding) {
    const onboardingPathToRedirectTo = doesUserHaveName
      ? routes.onboarding.createShop
      : routes.onboarding.userInfo;

    const redirectUrl = `${onboardingPathToRedirectTo}?${searchParams.toString()}`;

    return <Navigate to={redirectUrl} replace />;
  }

  return (
    <>
      {isUsingBackendStaging && <BackendStagingBanner />}
      <PylonChatHider />
      <CommandMenu />
      <DebugCommandMenuItemsProvider />
      {children}
    </>
  );
}

function RootRouteIndexRedirectRoute() {
  const templateId = useTemplateIdFromSearchParams();
  const { data: workspaceData, isLoading: isLoadingWorkspaces } =
    workspaceRouter.getUserWorkspacesList.useQuery();

  if (isLoadingWorkspaces) {
    return <FullScreenLoader />;
  }

  if (templateId) {
    return (
      <Navigate
        to={generatePath(routes.home.shareTemplate, { templateId })}
        replace
      />
    );
  }

  const userHasOnlyOneWorkspace =
    (workspaceData?.workspaces ?? []).length === 1;
  const toUrl =
    userHasOnlyOneWorkspace && workspaceData?.workspaces![0]!.id
      ? generatePath("/workspace/:workspaceId/projects", {
          workspaceId: workspaceData.workspaces[0].id,
        })
      : routes.home.root;

  return <Navigate to={toUrl} replace />;
}

function PartnerProgramRedirectRoute() {
  const { user } = useCurrentUser();

  return (
    <Navigate
      to={
        user!.workspace?.id
          ? generatePath("/workspace/:workspaceId/partner/program", {
              workspaceId: user!.workspace.id,
            })
          : routes.home.root
      }
      replace
    />
  );
}

function WorkspaceIndexRedirectRoute() {
  const workspaceId = useCurrentWorkspaceId();
  return (
    <Navigate
      to={
        workspaceId
          ? generatePath(routes.workspace.projects, {
              workspaceId,
            })
          : routes.home.root
      }
      replace
    />
  );
}

function useTemplateIdFromSearchParams() {
  const [searchParams] = useSearchParams();
  return searchParams.get("templateId");
}

function ProjectRouteWrapper() {
  const { projectId } = useParams<{ projectId: string }>();

  if (!projectId) {
    return <Navigate to={routes.home.root} replace />;
  }

  return (
    <CurrentProjectProvider projectId={projectId} saveProjectIdAsLastVisited>
      <ProjectContentWithRedirect projectId={projectId} />
    </CurrentProjectProvider>
  );
}

/**
 * Redirect component for /editor/:projectId (no elementId).
 * Picks the last-selected element from localStorage, or the first element,
 * and redirects to /editor/:projectId/:elementId.
 */
function EditorIndexRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { elementMetadata, isElementMetadataSuccess } =
    useCurrentProjectContext();

  if (!projectId || !isElementMetadataSuccess || !elementMetadata) {
    return null;
  }

  const lastElementId = getLastSelectedElementIdForProject(projectId);
  const validLastElement =
    lastElementId &&
    elementMetadata.some(
      (e) => e.id === lastElementId && e.projectId === projectId,
    );
  const targetElementId = validLastElement
    ? lastElementId
    : elementMetadata[0]?.id;

  const searchString = searchParams.toString();
  const suffix = searchString ? `?${searchString}` : "";

  if (!targetElementId) {
    return <Navigate to={`/editor/${projectId}/new${suffix}`} replace />;
  }

  return (
    <Navigate to={`/editor/${projectId}/${targetElementId}${suffix}`} replace />
  );
}

function AgentProjectRouteWrapper() {
  const { projectId } = useParams<{ projectId: string }>();

  if (!projectId) {
    return <Navigate to={routes.home.root} replace />;
  }

  return (
    <AgentLocalStorageProvider>
      <CurrentProjectProvider projectId={projectId} saveProjectIdAsLastVisited>
        <AgentProjectRouteGuard />
      </CurrentProjectProvider>
    </AgentLocalStorageProvider>
  );
}

function AgentProjectRouteGuard() {
  const { project } = useCurrentProjectContext();

  if (!project) {
    return <FullScreenLoader />;
  }

  if (project.hostingType !== "replo") {
    return (
      <Navigate
        to={generatePath(routes.shops.site, { projectId: project.id })}
        replace
      />
    );
  }

  if (!project.agentEnabledAt) {
    return (
      <Navigate
        to={generatePath(routes.shops.home, { projectId: project.id })}
        replace
      />
    );
  }

  return (
    <ScopedAnalyticsProvider>
      <AgentEditorStoreProvider>
        <AgentSandboxStateProvider>
          <AgentNextDevServerProvider>
            <AgentModifiersProvider>
              <AgentChatStoreProvider>
                <AgentCurrentChatProvider>
                  <AgentSessionProvider>
                    <Outlet />
                  </AgentSessionProvider>
                </AgentCurrentChatProvider>
              </AgentChatStoreProvider>
            </AgentModifiersProvider>
          </AgentNextDevServerProvider>
        </AgentSandboxStateProvider>
      </AgentEditorStoreProvider>
    </ScopedAnalyticsProvider>
  );
}

/**
 * Component that handles the project content and marketing seed redirects
 */
function ProjectContentWithRedirect({ projectId }: { projectId: string }) {
  const { project, isLoading } = useCurrentProjectContext();
  const { pathname } = useLocation();

  // Note (Ezra, 2025-11-11): Apply shared template after auth - moved here from RootRouteLayout
  // to match marketing seed pattern. Only runs when on a project route, so onboarding checks
  // are not needed.
  useApplySharedTemplateAfterAuth();

  if (isLoading) {
    return null;
  }

  const marketingSeedData = localStorage.getItem("replo_marketing_seed")
    ? JSON.parse(localStorage.getItem("replo_marketing_seed")!)
    : null;

  if (
    marketingSeedData &&
    project?.hostingType === "replo" &&
    !pathname.includes("/chat") &&
    project.styleLibrary
  ) {
    return <Navigate to={`/shops/${projectId}/chat`} replace />;
  }

  return (
    <ScopedAnalyticsProvider>
      {/* TODO (Gabe 2025-06-18): This thing will rerender on log output which is not great. */}
      <EditorStoreProvider>
        <SandboxStateProvider>
          <NextDevServerProvider>
            <CurrentChatProvider>
              <InitialProjectSetupModal projectId={projectId} />
              <Outlet />
            </CurrentChatProvider>
          </NextDevServerProvider>
        </SandboxStateProvider>
      </EditorStoreProvider>
    </ScopedAnalyticsProvider>
  );
}

/**
 * This modal is used to check for full project onboarding status and show modals as needed
 */
function InitialProjectSetupModal({ projectId }: { projectId: string }) {
  const { modalToShow } = useSelectInitialProjectSetupModal();

  if (modalToShow === null) {
    return null;
  }

  return exhaustiveSwitch({ type: modalToShow })({
    starterSites: <StarterProjectsModal projectId={projectId} />,
  });
}
